import type { Locale } from './config';
import { defaultLocale, localizedPathAliases } from './config';

const EN_PREFIX = '/en';

export function getLocaleFromPath(pathname: string): Locale {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`) ? 'en' : 'it';
}

export function getLocalePrefix(locale: Locale): string {
  return locale === 'en' ? EN_PREFIX : '';
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function resolveLogicalPath(pathname: string): string {
  const normalized = normalizePath(pathname);
  for (const [logical, aliases] of Object.entries(localizedPathAliases)) {
    if (normalized === aliases.it || normalized === aliases.en) return logical;
  }
  if (normalized === EN_PREFIX) return '/';
  if (normalized.startsWith(`${EN_PREFIX}/`)) {
    return normalized.slice(EN_PREFIX.length) || '/';
  }
  return normalized;
}

/** Strip locale prefix to get the logical path (e.g. /en/menu → /menu) */
export function stripLocaleFromPath(pathname: string): string {
  return resolveLogicalPath(pathname);
}

/** Build localized path from a logical path */
export function localizedPath(logicalPath: string, locale: Locale): string {
  const normalized = normalizePath(logicalPath);
  const alias = localizedPathAliases[normalized];
  if (alias) return alias[locale];
  if (locale === defaultLocale) return normalized;
  if (normalized === '/') return EN_PREFIX;
  return `${EN_PREFIX}${normalized}`;
}

/** Switch locale while preserving logical page + hash */
export function switchLocalePath(currentPathname: string, currentHash: string, targetLocale: Locale): string {
  const logical = stripLocaleFromPath(currentPathname);
  return `${localizedPath(logical, targetLocale)}${currentHash}`;
}

/** SEO alternate paths for a page */
export function getAlternatePaths(logicalPath: string): { it: string; en: string } {
  return {
    it: localizedPath(logicalPath, 'it'),
    en: localizedPath(logicalPath, 'en'),
  };
}
